/**
 * System Admin Authentication Context
 *
 * Manages system-admin auth state and login/logout for the /admin console.
 * Entirely separate from AuthContext (tenant users) - no shared state, no
 * shared storage key, no shared API client instance.
 */

import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { adminClient, AdminInfo } from '../api/adminClient';
import { decodeToken, isTokenValid } from '../utils/jwt';
import * as adminStorage from '../utils/adminStorage';

interface AdminTokenPayload {
  adminId: string;
  email: string;
  type: string;
  iat: number;
  exp: number;
}

export interface AdminAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  admin: AdminInfo | null;
  token: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

interface AdminAuthProviderProps {
  children: React.ReactNode;
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [admin, setAdmin] = useState<AdminInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Initialize: check if an admin is already logged in (token in localStorage)
  useEffect(() => {
    try {
      const savedToken = adminStorage.getAdminAuthToken();

      if (savedToken && isTokenValid(savedToken)) {
        const decoded = decodeToken(savedToken) as unknown as AdminTokenPayload | null;

        if (decoded) {
          setToken(savedToken);
          setAdmin({ id: decoded.adminId, email: decoded.email });
          setIsAuthenticated(true);
          adminClient.restoreAdminToken(savedToken);
        }
      } else if (savedToken) {
        adminStorage.clearAdminAuthStorage();
      }
    } catch (err) {
      console.error('Admin auth init error:', err);
      adminStorage.clearAdminAuthStorage();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await adminClient.adminLogin(email, password);

      adminClient.setAdminToken(response.token);

      setToken(response.token);
      setAdmin(response.admin);
      setIsAuthenticated(true);
    } catch (err) {
      const errorMessage =
        (err as any)?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Login failed');
      setError(errorMessage);
      setIsAuthenticated(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    adminClient.clearAdminToken();
    setToken(null);
    setAdmin(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AdminAuthContextType = {
    isAuthenticated,
    isLoading,
    error,
    admin,
    token,
    login,
    logout,
    clearError,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

/**
 * Hook to use admin auth context
 */
export function useAdminAuth(): AdminAuthContextType {
  const context = useContext(AdminAuthContext);

  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }

  return context;
}
